import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Mail, Bell, ChevronDown, User, Home, Settings, LogOut, ShieldCheck, Menu } from "lucide-react";
import { currentUser as fallbackUser } from "../data/mockData";
import { useAuth } from "../contexts/AuthContext";
import useMobile from "../hooks/useMobile";

export default function Topbar({ searchPlaceholder = "Search", onMenuOpen }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const display = user || fallbackUser;
    const avatar = user?.picture || fallbackUser.avatar;
    const [dropOpen, setDropOpen] = useState(false);
    const dropRef = useRef(null);
    const isMobile = useMobile();

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) {
                setDropOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleLogout = async () => {
        setDropOpen(false);
        await logout();
        navigate("/");
    };

    const isAdmin = user?.role === "admin";

    return (
        <header
            data-testid="app-topbar"
            style={{
                position: "sticky", top: 0, zIndex: 30,
                background: "rgba(10,11,14,0.85)",
                backdropFilter: "blur(12px)",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
            }}
        >
            <div style={{
                display: "flex", alignItems: "center",
                justifyContent: "space-between", gap: isMobile ? 10 : 24,
                padding: isMobile ? "0 12px" : "0 24px", height: 64
            }}>
                {/* Hamburger — mobile only */}
                {isMobile && onMenuOpen && (
                    <button
                        onClick={onMenuOpen}
                        aria-label="Open navigation"
                        style={{
                            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                            background: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#9ca3af", cursor: "pointer"
                        }}
                    >
                        <Menu size={18} />
                    </button>
                )}

                {/* Search — hidden on mobile */}
                {!isMobile && (
                    <div style={{ flex: 1, maxWidth: 560, position: "relative" }}>
                        <Search size={15} style={{
                            position: "absolute", left: 16,
                            top: "50%", transform: "translateY(-50%)",
                            color: "#6b7280"
                        }} />
                        <input
                            data-testid="topbar-search"
                            type="text"
                            placeholder={searchPlaceholder}
                            style={{
                                width: "100%", boxSizing: "border-box",
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: 99, padding: "10px 16px 10px 42px",
                                color: "#fff", fontSize: 13, outline: "none",
                                transition: "border-color 0.2s"
                            }}
                            onFocus={(e) => e.target.style.borderColor = "rgba(207,255,80,0.4)"}
                            onBlur={(e) => e.target.style.borderColor = "rgba(255,255,255,0.08)"}
                        />
                    </div>
                )}

                {/* Right side */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: isMobile ? "auto" : undefined }}>
                    {!isMobile && (
                        <>
                            <Link
                                to="/"
                                data-testid="topbar-home"
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    height: 36, padding: "0 14px", borderRadius: 99,
                                    background: "rgba(255,255,255,0.06)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    color: "#d1d5db", fontSize: 13, fontWeight: 600,
                                    textDecoration: "none", transition: "all 0.2s"
                                }}
                            >
                                <Home size={14} />
                                <span>Home</span>
                            </Link>

                            <button
                                data-testid="topbar-mail"
                                style={{
                                    width: 36, height: 36, borderRadius: 10,
                                    background: "rgba(255,255,255,0.06)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#9ca3af", cursor: "pointer"
                                }}
                            >
                                <Mail size={15} />
                            </button>

                            <button
                                data-testid="topbar-notifications"
                                style={{
                                    width: 36, height: 36, borderRadius: 10,
                                    background: "rgba(255,255,255,0.06)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#9ca3af", cursor: "pointer", position: "relative"
                                }}
                            >
                                <Bell size={15} />
                                <span style={{
                                    position: "absolute", top: 8, right: 8,
                                    width: 6, height: 6, borderRadius: "50%",
                                    background: "#CFFF50"
                                }} />
                            </button>
                        </>
                    )}

                    {/* User dropdown */}
                    {user ? (
                        <div ref={dropRef} style={{ position: "relative" }}>
                            <button
                                data-testid="topbar-user"
                                onClick={() => setDropOpen((o) => !o)}
                                style={{
                                    display: "flex", alignItems: "center", gap: isMobile ? 0 : 10,
                                    padding: isMobile ? "4px" : "4px 12px 4px 4px", borderRadius: 99,
                                    background: "rgba(255,255,255,0.06)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    cursor: "pointer", color: "#fff"
                                }}
                            >
                                {avatar && avatar.startsWith("http") ? (
                                    <img
                                        src={avatar}
                                        alt={display.name}
                                        style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }}
                                    />
                                ) : (
                                    <div style={{
                                        width: 28, height: 28, borderRadius: "50%",
                                        background: "#7C5CFF", display: "flex",
                                        alignItems: "center", justifyContent: "center",
                                        fontSize: 11, fontWeight: 800, color: "#fff"
                                    }}>
                                        {display.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                                    </div>
                                )}
                                {!isMobile && (
                                    <>
                                        <div style={{ textAlign: "left" }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{display.name}</div>
                                            <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.2 }}>{display.email}</div>
                                        </div>
                                        <ChevronDown
                                            size={14}
                                            color="#6b7280"
                                            style={{ transition: "transform 0.2s", transform: dropOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                                        />
                                    </>
                                )}
                            </button>

                            {/* Dropdown menu */}
                            {dropOpen && (
                                <div style={{
                                    position: "absolute", top: "calc(100% + 8px)", right: 0,
                                    width: 220, background: "#111318",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: 16, overflow: "hidden",
                                    boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
                                    zIndex: 100
                                }}>
                                    {/* User info */}
                                    <div style={{
                                        padding: "14px 16px",
                                        borderBottom: "1px solid rgba(255,255,255,0.07)"
                                    }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 2 }}>{display.name}</div>
                                        <div style={{ fontSize: 11, color: "#6b7280" }}>{display.email}</div>
                                        {isAdmin && (
                                            <div style={{
                                                marginTop: 8, display: "inline-flex",
                                                alignItems: "center", gap: 4,
                                                padding: "3px 8px", borderRadius: 99,
                                                background: "rgba(124,92,255,0.15)",
                                                border: "1px solid rgba(124,92,255,0.3)",
                                                fontSize: 10, fontWeight: 700,
                                                color: "#7C5CFF", letterSpacing: "0.05em"
                                            }}>
                                                <ShieldCheck size={10} /> ADMIN
                                            </div>
                                        )}
                                    </div>

                                    {/* Menu items */}
                                    <div style={{ padding: "6px 0" }}>
                                        <Link
                                            to="/settings"
                                            onClick={() => setDropOpen(false)}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 10,
                                                padding: "10px 16px", color: "#d1d5db",
                                                fontSize: 13, fontWeight: 500,
                                                textDecoration: "none", transition: "background 0.15s"
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                        >
                                            <User size={14} color="#6b7280" /> Profile & Settings
                                        </Link>

                                        {isAdmin && (
                                            <Link
                                                to="/admin"
                                                onClick={() => setDropOpen(false)}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 10,
                                                    padding: "10px 16px", color: "#d1d5db",
                                                    fontSize: 13, fontWeight: 500,
                                                    textDecoration: "none", transition: "background 0.15s"
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                                                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                            >
                                                <ShieldCheck size={14} color="#7C5CFF" /> Admin panel
                                            </Link>
                                        )}

                                        <div style={{ margin: "4px 0", borderTop: "1px solid rgba(255,255,255,0.07)" }} />

                                        <button
                                            onClick={handleLogout}
                                            data-testid="logout-btn"
                                            style={{
                                                width: "100%", display: "flex", alignItems: "center", gap: 10,
                                                padding: "10px 16px", color: "#ef4444",
                                                fontSize: 13, fontWeight: 500,
                                                background: "transparent", border: "none",
                                                cursor: "pointer", textAlign: "left",
                                                transition: "background 0.15s"
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(239,68,68,0.08)"}
                                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                        >
                                            <LogOut size={14} /> Log out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Link
                            to="/login"
                            data-testid="topbar-signin"
                            style={{
                                padding: "8px 20px", borderRadius: 99,
                                background: "#CFFF50", color: "#0A0B0E",
                                fontSize: 13, fontWeight: 800,
                                textDecoration: "none"
                            }}
                        >
                            Sign in
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
}
